import React, { useState, useEffect, forwardRef, useRef } from "react";
import {
  Autocomplete,
  Box,
  Group,
  Loader,
  Select,
  Text,
  createStyles,
} from "@mantine/core";
import arrow from "../assets/img/selectArrow.svg";
import { cernerHandler, circularPlatformLogo, ecwHandler, epicHandler, getAccessToken, getIntegrationTypes, getTokenResponse, searchAthenaPatient } from "../utils";
import _ from "lodash";
import { useMedplum } from "@medplum/react";
import { PatientResource } from "./PatientInfoModal";
import { ExtensionObject, getTokensAndUrl } from "../pages/HomePage.utils";
import { showNotification } from "@mantine/notifications";
import { useSelector } from "react-redux";
import { AppState } from "../store/types/appState";
import { useDispatch } from "react-redux";
const useStyles = createStyles(() => ({
  container: {
    maxWidth: "438px",
    width: "438px",
    maxHeight: "50px",
    minHeight: "50px",
    display: "flex",
    alignItems: "center",
    border: "1px solid #C9CDD3",
    borderRadius: "8px",
    position: "relative",
  },
  select: {
    paddingLeft: "10px",
    width: "150px",
    ".mantine-Select-dropdown": {
      width: "440px !important",
      left: "0 !important",
    },
    ".mantine-Select-input": {
      textTransform: "capitalize",
    },
  },
  selectLogo: {
    width: '50px',
    height: '40px',
  },
  autoComplete: {
    width: "300px",
    borderRadius: "5px",
    ".mantine-Autocomplete-dropdown": {
      width: "440px !important",
      left: "0 !important",
      height: "280px",
      overflow: "scroll",
    },
  },
}));

interface SearchPatientProps {
  setPatientToShow: (value: PatientResource) => void;
  setIsPatientModalOpen: (value: boolean) => void;
  setSelectedEHR: (value: string) => void;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  isPatientModalOpen: boolean;
}

const SearchPatientComponent: React.FC<SearchPatientProps> = ({
  setPatientToShow,
  setIsPatientModalOpen,
  setSelectedEHR,
  searchTerm,
  setSearchTerm,
  isPatientModalOpen,
}) => {
  const { classes } = useStyles();
  const medplum = useMedplum();
  const dispatch = useDispatch();
  const [autocompleteValue, setAutocompleteValue] = useState<
    string | undefined
  >();
  const [autocompleteData, setAutocompleteData] = useState([]);
  const [showSelectList, setShowSelectList] = useState(false);
  const [loading, setLoading] = useState(false);
  const logginedUser = useSelector((state: AppState) => state) as any;
  const automcompleteRef = useRef() as any;
  const ext = logginedUser?.app?.profile.extension?.filter((e: any) => {
    const display = e?.extension?.find((el: any) => el?.url === "display")?.valueString
    if (!display) return true
    return display?.toLowerCase().includes('provider') || display?.toLowerCase().includes('practioner')
  });

  const platforms = logginedUser
    ? getIntegrationTypes(ext as any).map((ele) => {
      return { image: ele, value: ele, label: ele };
    })
    : [];

  async function getPatientList(name: string) {
    setLoading(true);
    const activeLogin = getAccessToken();
    const tokensAndUrl = getTokensAndUrl(
      logginedUser?.app?.profile?.extension as ExtensionObject[],
      searchTerm
    );
    const tokenData = {
      access_token: tokensAndUrl?.[searchTerm]?.accessToken,
    }
    // await getTokenResponse(
    //   medplum,
    //   
    // )
    const meta = {
      dispatch,
      clientId: tokensAndUrl?.[searchTerm]?.clientId,
      integrationType: searchTerm.toLowerCase(),
      refreshToken: tokensAndUrl?.[searchTerm]?.refreshToken,
      activeLogin,
      name: tokensAndUrl?.[searchTerm]?.name
    }

    let res;

    if (searchTerm === "epic") {
      res = await searchEPICPatientWithMRN(tokenData?.access_token, name, meta);
    }
    else if (searchTerm === "cerner") {
      const payload = {
        accessToken: tokenData?.access_token,
        eventType: "patientList",
        name: name,
      };
      res = await cernerHandler(medplum, payload);
    }
    else if (searchTerm === "eclinicalworks") {
      const payload = {
        accessToken: tokenData?.access_token ?? tokensAndUrl?.[searchTerm]?.accessToken,
        clientId: tokensAndUrl?.[searchTerm]?.clientId,
        eventType: "patientList",
        name: name,
      };
      res = await ecwHandler(medplum, payload, meta);
    }
    else if (searchTerm === "athenahealth") {
      res = await searchAthenaPatient(medplum, tokenData?.access_token, name, meta);
    }
    
    if (res?.statusCode === 200 && res?.body?.entry?.length) {
      const names = res?.body?.entry?.map(
        (item: { resource: PatientResource }) => {
          return {
            resource: item?.resource,
            label: searchTerm === "cerner"
              ? item?.resource?.name[0]?.text
              : searchTerm === "athenahealth"
                ? item?.resource?.name[0]?.given?.join(" ")
                : `${item?.resource?.name[0]?.text}`,

            value: searchTerm === "cerner"
              ? item?.resource?.name[0]?.text.toLowerCase()
              : searchTerm === "athenahealth" ? item?.resource?.name[0]?.given?.join(" ")
                : `${name} ${item?.resource?.name[0]?.text.toLowerCase()}`,
          };
        }
      );
      setAutocompleteData(names as any);
      setLoading(false);
    }
    else {
      setLoading(false);
      showNotification({ color: 'red', message: "No Result Found!", autoClose: true });
    }
  }

  async function searchEPICPatientWithMRN(accessToken: string, MRNNumber: string, meta:any) {
    const payload = {
      accessToken: accessToken,
      eventType: "searchPatient",
      MRNNumber,
    };
    return await epicHandler(medplum, payload, meta);
  }

  useEffect(() => {
    const debouncedGetPatientList = _.debounce(getPatientList, 2000);

    if (autocompleteValue?.length && !isPatientModalOpen) {
      debouncedGetPatientList(autocompleteValue);
    } else {
      setLoading(false);
    }

    return () => {
      debouncedGetPatientList.cancel();
    };
  }, [autocompleteValue]);

  const handleSelectChange = (value: string) => {
    if (!["athenahealth", "epic", "cerner", "eclinicalworks"].includes(value.toLowerCase())) {
      showNotification({ color: 'red', message: "EHR does not supports Search API", autoClose: true });
      return;
    }
    setSearchTerm(value);
    setSelectedEHR(value);
  };

  const handleAutocompleteChange = (value: string) => {
    setAutocompleteValue(value);
  };

  interface ItemProps extends React.ComponentPropsWithoutRef<"div"> {
    image: string;
    label: string;
    description: string;
  }

  const SelectItem = forwardRef<HTMLDivElement, ItemProps>(
    ({ image, label, description, ...others }: ItemProps, ref) => (
      <>
        <div
          ref={ref}
          {...others}
          style={{
            borderBottom: "1px solid #E5E5E5",
          }}
        >
          <Group noWrap>
            <img src={circularPlatformLogo[image]} className={classes.selectLogo} />
            <div>
              <Text
                size="sm"
                sx={{
                  textTransform: "capitalize",
                }}
              >
                {label}
              </Text>
            </div>
          </Group>
        </div>
      </>
    )
  );

  const AutoSelectItem = forwardRef<HTMLDivElement, ItemProps>(
    ({ label, ...others }: ItemProps, ref) => (
      <>
        <div
          ref={ref}
          {...others}
          style={{
            borderBottom: "1px solid #E5E5E5",
            padding: "16px 10px",
          }}
        >
          <Group noWrap>
            <Box>
              <Text size="sm">{label}</Text>
            </Box>
          </Group>
        </div>
      </>
    )
  );

  useEffect(() => {
    if (searchTerm === "epic") {
      setAutocompleteValue("203713");
      automcompleteRef.current.focus();
    }
  }, [searchTerm])

  return (
    <Box className={classes.container}>
      <Select
        placeholder="EHR type"
        variant="unstyled"
        itemComponent={SelectItem}
        value={searchTerm}
        onChange={handleSelectChange}
        rightSection={
          <img
            src={arrow}
            onClick={() => setShowSelectList(!showSelectList)}
            style={{ cursor: "pointer" }}
          />
        }
        onClick={() => setShowSelectList(!showSelectList)}
        data={platforms}
        className={classes.select}

      />

      <Autocomplete
        data={autocompleteData}
        ref={automcompleteRef}
        variant="unstyled"
        placeholder={searchTerm === "epic" ? "Search Patients with MRN Number" : "Search Patients in EHR"}
        itemComponent={AutoSelectItem}
        value={autocompleteValue}
        onChange={handleAutocompleteChange}
        className={classes.autoComplete}
        limit={20}
        rightSection={loading ? <Loader size="1rem" /> : null}
        onItemSubmit={(item) => {
          setPatientToShow(item.resource);
          setIsPatientModalOpen(true);
        }}
        size="lg"
        radius={"md"}
      />
    </Box>
  );
};

export default SearchPatientComponent;
